import React from 'react';
import ReactDOM from 'react-dom';
import { withTranslation } from 'react-i18next';
import api from 'lib/api';
import { geocodingApi } from 'lib/api';
import Map, { Marker } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { HourInput } from 'views/offers/AddSteps/Step1';
import { useThemeMode } from 'theme/ThemeProvider';

import styles from './Step2.module.scss';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

const MAP_STYLE_LIGHT = 'mapbox://styles/mapbox/streets-v12';
const MAP_STYLE_DARK = 'mapbox://styles/mapbox/dark-v11';

class RegionSearch extends React.Component {
    constructor(props) {
        super(props);
        this.state = { query: props.selectedLabel || '', open: false };
        this.wrapRef = React.createRef();
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleOutside);
        window.addEventListener('scroll', this.handleScroll, true);
        window.addEventListener('resize', this.handleScroll);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleOutside);
        window.removeEventListener('scroll', this.handleScroll, true);
        window.removeEventListener('resize', this.handleScroll);
    }

    componentDidUpdate(prev) {
        if (prev.selectedLabel !== this.props.selectedLabel) {
            this.setState({ query: this.props.selectedLabel || '' });
        }
    }

    handleOutside = (e) => {
        if (this.wrapRef.current && !this.wrapRef.current.contains(e.target)) {
            this.setState({ open: false });
        }
    };

    handleScroll = () => {
        if (this.state.open) this.forceUpdate();
    };

    get filtered() {
        const q = this.state.query.trim().toLowerCase();
        const list = this.props.options || [];
        if (!q) return list.slice(0, 60);
        return list
            .filter((o) => String(o.postal_code).startsWith(q) || o.city.toLowerCase().includes(q))
            .slice(0, 60);
    }

    select = (option) => {
        this.setState({ query: `${option.postal_code} ${option.city}`, open: false });
        this.props.onChange(option);
    };

    getDropdownStyle() {
        if (!this.wrapRef.current) return {};
        const r = this.wrapRef.current.getBoundingClientRect();
        return {
            top: r.bottom + window.scrollY,
            left: r.left + window.scrollX,
            width: r.width,
        };
    }

    render() {
        const { placeholder } = this.props;
        const { query, open } = this.state;
        const items = this.filtered;
        const dropStyle = this.getDropdownStyle();

        const dropdown =
            open &&
            ReactDOM.createPortal(
                <div
                    className={styles.regionDropdown}
                    style={{ position: 'absolute', zIndex: 99999, ...dropStyle }}
                >
                    {items.length === 0 ? (
                        <div className={styles.regionEmpty}>Engar niðurstöður</div>
                    ) : (
                        items.map((o) => (
                            <div
                                key={o.id}
                                className={styles.regionOption}
                                onMouseDown={() => this.select(o)}
                            >
                                <span className={styles.regionPostal}>{o.postal_code}</span>
                                <span>{o.city}</span>
                            </div>
                        ))
                    )}
                </div>,
                document.body
            );

        return (
            <div className={styles.regionWrap} ref={this.wrapRef}>
                <input
                    className={styles.fieldInput}
                    value={query}
                    placeholder={placeholder}
                    autoComplete="off"
                    onChange={(e) => this.setState({ query: e.target.value, open: true })}
                    onFocus={() => this.setState({ open: true })}
                />
                {dropdown}
            </div>
        );
    }
}

function GeoConfirmModal({ lat, lng, address, onConfirm, onCancel }) {
    const [pos, setPos] = React.useState({ lat, lng });
    const { isDark } = useThemeMode();
    const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

    return ReactDOM.createPortal(
        <div className={styles.geoOverlay}>
            <div className={styles.geoDialog}>
                <div className={styles.geoDialogHeader}>
                    <div>
                        <h3 className={styles.geoDialogTitle}>Staðfesta staðsetningu</h3>
                        <p className={styles.geoDialogAddress}>{address}</p>
                    </div>
                    <button className={styles.geoCloseBtn} onClick={onCancel}>
                        ✕
                    </button>
                </div>
                <div className={styles.geoMapWrap}>
                    <Map
                        initialViewState={{ longitude: lng, latitude: lat, zoom: 15 }}
                        style={{ width: '100%', height: '100%' }}
                        mapStyle={isDark ? MAP_STYLE_DARK : MAP_STYLE_LIGHT}
                        mapboxAccessToken={MAPBOX_TOKEN}
                    >
                        <Marker
                            longitude={pos.lng}
                            latitude={pos.lat}
                            anchor="bottom"
                            draggable
                            onDragEnd={(e) => setPos({ lat: e.lngLat.lat, lng: e.lngLat.lng })}
                        >
                            <div className={styles.geoPin}>📍</div>
                        </Marker>
                    </Map>
                </div>
                <p className={styles.geoHint}>Dragðu pinnann til að leiðrétta staðsetningu</p>
                <div className={styles.geoActions}>
                    <button className={styles.geoCancelBtn} onClick={onCancel}>
                        Hætta við
                    </button>
                    <button
                        className={styles.geoConfirmBtn}
                        onClick={() => onConfirm(pos.lat, pos.lng)}
                    >
                        ✓ Staðfesta hnit
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

const toHHmm = (val) => (val || '').substring(0, 5);

const DAY_SHORT_KEYS = [
    'day_short_mon',
    'day_short_tue',
    'day_short_wed',
    'day_short_thu',
    'day_short_fri',
    'day_short_sat',
    'day_short_sun',
];

const EMPTY_OPENING = (name = '') => [
    {
        name,
        day: 0,
        day_txt: 'mán.',
        open_time: '08:00',
        close_time: '18:00',
        active: false,
        active24: false,
    },
    {
        name,
        day: 1,
        day_txt: 'þri.',
        open_time: '08:00',
        close_time: '18:00',
        active: false,
        active24: false,
    },
    {
        name,
        day: 2,
        day_txt: 'mið.',
        open_time: '08:00',
        close_time: '18:00',
        active: false,
        active24: false,
    },
    {
        name,
        day: 3,
        day_txt: 'fim.',
        open_time: '08:00',
        close_time: '18:00',
        active: false,
        active24: false,
    },
    {
        name,
        day: 4,
        day_txt: 'fös.',
        open_time: '08:00',
        close_time: '18:00',
        active: false,
        active24: false,
    },
    {
        name,
        day: 5,
        day_txt: 'lau.',
        open_time: '08:00',
        close_time: '18:00',
        active: false,
        active24: false,
    },
    {
        name,
        day: 6,
        day_txt: 'sun.',
        open_time: '08:00',
        close_time: '18:00',
        active: false,
        active24: false,
    },
];

class Step2 extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            notifDialog: { open: false, message: '' },
            geoModal: { open: false, lat: null, lng: null, index: null, address: '' },
            locations: [],
            changedStatus: false,
            collapsed: {},
        };
    }

    static getDerivedStateFromProps(props, state) {
        if (!props.company) return null;

        if (props.company.company_from === 'Torgid') {
            let locs = props.company.torgid_company_locations;
            if (
                (locs.length > 0 &&
                    state.locations.length === 0 &&
                    state.changedStatus === false) ||
                (locs.length > 0 &&
                    state.locations.length > 0 &&
                    locs[0].id !== state.locations[0].loc_id)
            ) {
                let locations = [];
                props.company.torgid_company_locations.forEach((value) => {
                    const activeTimes = [];
                    let openings = value.torgid_company_openings.reduce((r, a) => {
                        r[a.name] = [...(r[a.name] || []), a];
                        return r;
                    }, {});
                    openings &&
                        Object.keys(openings).map(function (items) {
                            const name = openings[items]?.[0]?.name || '';
                            const activeTime = EMPTY_OPENING(name);
                            openings[items].forEach((item) => {
                                activeTime[item.day].nameState = 'success';
                                activeTime[item.day].active = true;
                                activeTime[item.day].active24 = false;
                                activeTime[item.day].open_time = toHHmm(item.open_time);
                                activeTime[item.day].close_time = toHHmm(item.close_time);
                            });
                            activeTimes.push(activeTime);
                        });
                    locations.push({
                        loc_id: value.id,
                        id: value.torgid_region?.id || '',
                        city: value.torgid_region?.city || '',
                        postal_code: value.torgid_region?.postal_code || '',
                        torgid_region_id: value.torgid_region?.id || '',
                        address: value.address,
                        name: value.name,
                        phone: value.phone,
                        description: value.description,
                        latitude: value.latitude,
                        longitude: value.longitude,
                        torgid_company_openings:
                            activeTimes.length > 0 ? activeTimes : [EMPTY_OPENING()],
                    });
                });
                return { locations };
            }
        } else if (props.company.company_from === '1819') {
            const branches = props.company.branches;
            const hasAddress = props.company.address?.length > 0;
            if (
                (branches.length > 0 &&
                    state.locations.length === 0 &&
                    state.changedStatus === false) ||
                (branches.length === 0 &&
                    state.locations.length === 0 &&
                    hasAddress &&
                    props.company.city?.length > 0 &&
                    props.company.zip > 0) ||
                (branches.length > 0 &&
                    state.locations.length > 0 &&
                    branches[0].id !== state.locations[0].loc_1819_id)
            ) {
                let locations = [];
                if (hasAddress) {
                    let reg_id = '';
                    const regionList = Array.isArray(props.data) ? props.data : [];
                    if (regionList.length && props.company.city && props.company.zip) {
                        reg_id = regionList.find(
                            (el) =>
                                el.city === props.company.city &&
                                String(el.postal_code) === String(props.company.zip)
                        );
                    }
                    const activeTime = EMPTY_OPENING();
                    props.company.openings.forEach((item) => {
                        activeTime[item.day_id].nameState = 'success';
                        activeTime[item.day_id].active = true;
                        activeTime[item.day_id].open_time = item.opening_time;
                        activeTime[item.day_id].close_time = item.closing_time;
                    });
                    locations.push({
                        loc_1819_id: props.company.id,
                        loc_id: '',
                        id: reg_id ? reg_id.id : '',
                        torgid_region_id: reg_id ? reg_id.id : '',
                        city: props.company.city || '',
                        postal_code: props.company.zip || '',
                        address: props.company.address + ' ' + props.company.address_nr || '',
                        name: '',
                        phone: '',
                        description: '',
                        latitude: props.company.latitude || '',
                        longitude: props.company.longitude || '',
                        torgid_company_openings: [activeTime],
                    });
                }

                branches.forEach((value) => {
                    let reg_id = '';
                    const regionList2 = Array.isArray(props.data) ? props.data : [];
                    if (regionList2.length && value.city && value.contacts_zip_id) {
                        reg_id = regionList2.find(
                            (el) =>
                                el.city === value.city &&
                                String(el.postal_code) === String(value.contacts_zip_id)
                        );
                    }
                    const activeTime = EMPTY_OPENING();
                    value.openings.forEach((item) => {
                        activeTime[item.day_id].nameState = 'success';
                        activeTime[item.day_id].active = true;
                        activeTime[item.day_id].open_time = item.opening_time;
                        activeTime[item.day_id].close_time = item.closing_time;
                    });
                    locations.push({
                        loc_1819_id: branches[0].id,
                        loc_id: '',
                        id: reg_id ? reg_id.id : '',
                        torgid_region_id: reg_id ? reg_id.id : '',
                        city: value.city || '',
                        postal_code: value.contacts_zip_id || '',
                        address: value.address + ' ' + value.address_nr || '',
                        name: value.name || '',
                        phone: value.phone || '',
                        description: value.description || '',
                        latitude: value.latitude || '',
                        longitude: value.longitude || '',
                        torgid_company_openings: [activeTime],
                    });
                });
                return { locations, changedStatus: true };
            }
        }
        return null;
    }

    componentDidMount() {
        this._locationRefs = [];
        this.applyFocusLocation();
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps.focusLocationId !== this.props.focusLocationId ||
            prevState.locations !== this.state.locations
        ) {
            this.applyFocusLocation();
        }

        if (prevState === this.state) return;
        // Skip the initial data population via getDerivedStateFromProps
        if (!prevState.changedStatus && this.state.changedStatus) return;
        if (this.props.onFieldChange) {
            this.props.onFieldChange();
        }
    }

    applyFocusLocation = () => {
        const focusId = this.props.focusLocationId;
        if (!focusId || !this.state.locations?.length) return;
        if (this._focusedLocationId === String(focusId)) return;

        const index = this.state.locations.findIndex(
            (loc) => String(loc.loc_id) === String(focusId)
        );
        if (index < 0) return;

        this._focusedLocationId = String(focusId);
        const collapsed = {};
        this.state.locations.forEach((_, i) => {
            collapsed[i] = i !== index;
        });
        this.setState({ collapsed, focusedLocationIndex: index }, () => {
            requestAnimationFrame(() => {
                const el = this._locationRefs?.[index];
                if (el?.scrollIntoView) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            });
        });
    };

    sendState() {
        return this.state;
    }
    isValidated() {
        return true;
    }

    getCoordinates = (loc, index) => {
        if (!loc.address) return;
        const fullAddress = [loc.address, loc.postal_code, loc.city].filter(Boolean).join(' ');
        api.get(geocodingApi.geocode(fullAddress))
            .then((response) => {
                const { lat, lng } = response.data.results;
                this.setState({ geoModal: { open: true, lat, lng, index, address: fullAddress } });
            })
            .catch((err) => {
                const status = err?.response?.status;
                const msg = status === 404 ? 'Hnit fundust ekki!' : 'Eitthvað fór úrskeiðis!';
                this.setState({ notifDialog: { open: true, message: msg } });
                setTimeout(
                    () => this.setState({ notifDialog: { open: false, message: '' } }),
                    2000
                );
            });
    };

    confirmCoordinates = (lat, lng) => {
        const { index } = this.state.geoModal;
        const locations = this.state.locations.map((loc, i) =>
            i === index ? { ...loc, latitude: lat, longitude: lng } : loc
        );
        this.setState({
            locations,
            geoModal: { open: false, lat: null, lng: null, index: null, address: '' },
        });
    };

    closeGeoModal = () => {
        this.setState({
            geoModal: { open: false, lat: null, lng: null, index: null, address: '' },
        });
    };

    addLocation = () => {
        const locations = [
            ...this.state.locations,
            {
                torgid_region_id: '',
                address: '',
                name: '',
                phone: '',
                description: '',
                latitude: '',
                longitude: '',
                id: '',
                postal_code: '',
                city: '',
                torgid_company_openings: [EMPTY_OPENING()],
            },
        ];
        this.setState({ locations });
    };

    removeLocation = (index) => {
        const locations = [...this.state.locations];
        locations.splice(index, 1);
        this.setState({ locations, changedStatus: true });
    };

    handleChange = (value, field, locIndex) => {
        const locations = this.state.locations.map((loc, i) =>
            i === locIndex ? { ...loc, [field]: value } : loc
        );
        this.setState({ locations });
    };

    handleChangeRegion = (option, locIndex) => {
        if (!option) return;
        const locations = this.state.locations.map((loc, i) =>
            i === locIndex
                ? {
                      ...loc,
                      torgid_region_id: option.id,
                      id: option.id,
                      postal_code: option.postal_code,
                      city: option.city,
                  }
                : loc
        );
        this.setState({ locations });
    };

    updateOpening = (locIndex, openIndex, dayIndex, updater) => {
        const locations = this.state.locations.map((loc, li) => {
            if (li !== locIndex) return loc;
            return {
                ...loc,
                torgid_company_openings: loc.torgid_company_openings.map((schedule, oi) => {
                    if (oi !== openIndex) return schedule;
                    return schedule.map((day, di) => (di === dayIndex ? updater(day) : day));
                }),
            };
        });
        this.setState({ locations });
    };

    addMoreActiveDays = (locIndex) => {
        const locations = this.state.locations.map((loc, li) =>
            li !== locIndex
                ? loc
                : {
                      ...loc,
                      torgid_company_openings: [...loc.torgid_company_openings, EMPTY_OPENING()],
                  }
        );
        this.setState({ locations });
    };

    removeActiveDays = (locIndex, openIndex) => {
        const locations = this.state.locations.map((loc, li) =>
            li !== locIndex
                ? loc
                : {
                      ...loc,
                      torgid_company_openings: loc.torgid_company_openings.filter(
                          (_, oi) => oi !== openIndex
                      ),
                  }
        );
        this.setState({ locations });
    };

    handleActiveTime = (locIndex, openIndex, dayIndex) => {
        this.updateOpening(locIndex, openIndex, dayIndex, (day) => ({
            ...day,
            active: !day.active,
        }));
    };

    handleTime24 = (locIndex, openIndex, dayIndex) => {
        this.updateOpening(locIndex, openIndex, dayIndex, (day) => ({
            ...day,
            open_time: '00:00',
            close_time: '23:59',
            active24: !day.active24,
        }));
    };

    handleChangeTime = (val, locIndex, openIndex, dayIndex, type) => {
        this.updateOpening(locIndex, openIndex, dayIndex, (day) => ({
            ...day,
            open_time: type === 'st' ? val : day.open_time,
            close_time: type === 'et' ? val : day.close_time,
            active: true,
            active24: false,
        }));
    };

    toggleCollapse = (index) => {
        this.setState((prev) => ({
            collapsed: { ...prev.collapsed, [index]: !prev.collapsed[index] },
        }));
    };

    render() {
        const { data, t } = this.props;
        const options = Array.isArray(data) ? data : [];

        return (
            <div className={styles.container}>
                <ConfirmDialog
                    open={this.state.notifDialog.open}
                    type="warning"
                    title={this.state.notifDialog.message}
                    onConfirm={() => this.setState({ notifDialog: { open: false, message: '' } })}
                />

                {this.state.geoModal.open && (
                    <GeoConfirmModal
                        lat={this.state.geoModal.lat}
                        lng={this.state.geoModal.lng}
                        address={this.state.geoModal.address}
                        onConfirm={this.confirmCoordinates}
                        onCancel={this.closeGeoModal}
                    />
                )}

                {this.state.locations.map((loc, locIndex) => {
                    const defaultCollapsed = this.state.locations.length > 2;
                    const isCollapsed = this.state.collapsed[locIndex] !== undefined
                        ? this.state.collapsed[locIndex]
                        : defaultCollapsed;
                    const isFocused = this.state.focusedLocationIndex === locIndex;
                    return (
                        <div
                            key={loc.loc_id || locIndex}
                            className={`${styles.locationCard} ${
                                isFocused ? styles.locationCardFocused : ''
                            }`}
                            ref={(el) => {
                                if (!this._locationRefs) this._locationRefs = [];
                                this._locationRefs[locIndex] = el;
                            }}
                        >
                            {/* Header */}
                            <div
                                className={styles.locationHeader}
                                onClick={() => this.toggleCollapse(locIndex)}
                            >
                                <h4 className={styles.locationTitle}>
                                    {loc.name ||
                                        [
                                            loc.address,
                                            loc.postal_code
                                                ? `${loc.postal_code} ${loc.city}`
                                                : loc.city,
                                        ]
                                            .filter(Boolean)
                                            .join(', ') ||
                                        `${t('location')} ${locIndex + 1}`}
                                </h4>
                                <div className={styles.locationHeaderActions}>
                                    <button
                                        className={styles.removeLocBtn}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            this.removeLocation(locIndex);
                                        }}
                                    >
                                        ✕ {t('company.location.remove')}
                                    </button>
                                    <span
                                        className={`${styles.chevron} ${
                                            isCollapsed ? '' : styles.open
                                        }`}
                                    >
                                        ▾
                                    </span>
                                </div>
                            </div>

                            {/* Body */}
                            {!isCollapsed && (
                                <div className={styles.locationBody}>
                                    {/* Row 1: Address + Region */}
                                    <div className={styles.fieldGrid}>
                                        <div className={styles.fieldGroup}>
                                            <label className={styles.fieldLabel}>
                                                {t('address')}
                                            </label>
                                            <input
                                                className={styles.fieldInput}
                                                value={loc.address}
                                                placeholder="Laugavegur 1"
                                                onChange={(e) =>
                                                    this.handleChange(
                                                        e.target.value,
                                                        'address',
                                                        locIndex
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className={styles.fieldGroup}>
                                            <label className={styles.fieldLabel}>
                                                {t('company.select_region')}
                                            </label>
                                            <RegionSearch
                                                options={options}
                                                selectedLabel={
                                                    loc.id ? `${loc.postal_code} ${loc.city}` : ''
                                                }
                                                placeholder={t('company.select_region')}
                                                onChange={(opt) =>
                                                    this.handleChangeRegion(opt, locIndex)
                                                }
                                            />
                                        </div>
                                    </div>

                                    {/* Row 2: Name */}
                                    <div className={styles.fieldGroup}>
                                        <label className={styles.fieldLabel}>
                                            {t('location.name')}
                                        </label>
                                        <input
                                            className={styles.fieldInput}
                                            value={loc.name}
                                            placeholder={
                                                [
                                                    loc.address,
                                                    loc.postal_code
                                                        ? `${loc.postal_code} ${loc.city}`
                                                        : loc.city,
                                                ]
                                                    .filter(Boolean)
                                                    .join(', ') || t('location.name')
                                            }
                                            onChange={(e) =>
                                                this.handleChange(e.target.value, 'name', locIndex)
                                            }
                                        />
                                    </div>

                                    {/* Row 3: Lat + Lng + Coord buttons */}
                                    <div className={styles.fieldGrid3}>
                                        <div className={styles.fieldGroup}>
                                            <label className={styles.fieldLabel}>
                                                {t('location.latitude')}
                                            </label>
                                            <input
                                                className={styles.fieldInput}
                                                value={loc.latitude}
                                                placeholder="64.1355"
                                                onChange={(e) =>
                                                    this.handleChange(
                                                        e.target.value,
                                                        'latitude',
                                                        locIndex
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className={styles.fieldGroup}>
                                            <label className={styles.fieldLabel}>
                                                {t('location.longitude')}
                                            </label>
                                            <input
                                                className={styles.fieldInput}
                                                value={loc.longitude}
                                                placeholder="-21.8954"
                                                onChange={(e) =>
                                                    this.handleChange(
                                                        e.target.value,
                                                        'longitude',
                                                        locIndex
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className={styles.fieldGroup}>
                                            <label className={styles.fieldLabel}>&nbsp;</label>
                                            <div className={styles.coordBtnRow}>
                                                <button
                                                    className={`${styles.coordBtn} ${styles.primary}`}
                                                    disabled={!loc.address}
                                                    onClick={() =>
                                                        this.getCoordinates(loc, locIndex)
                                                    }
                                                >
                                                    🎯 {t('location.coordinates_auto')}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <div className={styles.fieldGroup}>
                                        <label className={styles.fieldLabel}>
                                            {t('description')}
                                        </label>
                                        <input
                                            className={styles.fieldInput}
                                            value={loc.description}
                                            placeholder={t('description')}
                                            onChange={(e) =>
                                                this.handleChange(
                                                    e.target.value,
                                                    'description',
                                                    locIndex
                                                )
                                            }
                                        />
                                    </div>

                                    <hr className={styles.divider} />

                                    {/* Opening hours */}
                                    <p className={styles.sectionSubtitle}>{t('opening_hours')}</p>

                                    {loc.torgid_company_openings.map((schedule, openIndex) => (
                                        <div key={openIndex} className={styles.openingGroup}>
                                            {openIndex > 0 && (
                                                <div className={styles.openingGroupHeader}>
                                                    <button
                                                        className={styles.removeOpeningBtn}
                                                        onClick={() =>
                                                            this.removeActiveDays(
                                                                locIndex,
                                                                openIndex
                                                            )
                                                        }
                                                    >
                                                        ✕ {t('company.active.remove')}
                                                    </button>
                                                </div>
                                            )}

                                            <div className={styles.activeDaysList}>
                                                {schedule.map((day, dayIndex) => (
                                                    <div key={dayIndex} className={styles.dayRow}>
                                                        <button
                                                            type="button"
                                                            className={`${styles.dayToggle} ${
                                                                day.active
                                                                    ? styles.dayToggleActive
                                                                    : ''
                                                            }`}
                                                            onClick={() =>
                                                                this.handleActiveTime(
                                                                    locIndex,
                                                                    openIndex,
                                                                    dayIndex
                                                                )
                                                            }
                                                        >
                                                            {t(DAY_SHORT_KEYS[day.day] || day.day_txt)}
                                                        </button>
                                                        {day.active && (
                                                            <div className={styles.timeRange}>
                                                                <HourInput
                                                                    value={toHHmm(day.open_time)}
                                                                    disabled={day.active24}
                                                                    onChange={(val) =>
                                                                        this.handleChangeTime(
                                                                            val,
                                                                            locIndex,
                                                                            openIndex,
                                                                            dayIndex,
                                                                            'st'
                                                                        )
                                                                    }
                                                                />
                                                                <span className={styles.timeSep}>
                                                                    —
                                                                </span>
                                                                <HourInput
                                                                    value={toHHmm(day.close_time)}
                                                                    disabled={day.active24}
                                                                    onChange={(val) =>
                                                                        this.handleChangeTime(
                                                                            val,
                                                                            locIndex,
                                                                            openIndex,
                                                                            dayIndex,
                                                                            'et'
                                                                        )
                                                                    }
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className={`${
                                                                        styles.allDayBtn
                                                                    } ${
                                                                        day.active24
                                                                            ? styles.allDayBtnActive
                                                                            : ''
                                                                    }`}
                                                                    onClick={() =>
                                                                        this.handleTime24(
                                                                            locIndex,
                                                                            openIndex,
                                                                            dayIndex
                                                                        )
                                                                    }
                                                                >
                                                                    {day.active24 && '✓ '}
                                                                    {t('all_day')}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}

                                    <button
                                        className={styles.addOpeningBtn}
                                        onClick={() => this.addMoreActiveDays(locIndex)}
                                    >
                                        + {t('company.active.addmore')}
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Add location */}
                <button className={styles.addLocationBtn} onClick={this.addLocation}>
                    + {t('add_more_location')}
                </button>
            </div>
        );
    }
}

export default withTranslation('', { withRef: true })(Step2);
